<template>
  <div class="reviewable-extraction-field-group">
    <extraction-field-group-header v-if="headerText">
      {{ headerText }}
    </extraction-field-group-header>
    <v-expansion-panels
      v-model="expanded"
      accordion
      multiple
      class="flex-column fields"
    >
      <div
        v-for="(field, i) in dataFields"
        :key="field.id"
        class="flex-grow-1"
      >
        <reviewable-extraction-field
          :value="finalValue(field)"
          :field="field"
          :extraction-values="extractionValuesByFieldId[field.id]"
          :reports="reportsByFieldId[field.id]"
          :complete="fieldHasFinalValue(field.id)"
          :open="expanded.includes(i)"
          :readonly="readonly"
          :error="errorFieldIds.includes(field.id)"
          @input="confirmAndExpandNextField(i, field, $event)"
        />
      </div>
    </v-expansion-panels>
  </div>
</template>

<script>
import ExtractionFieldGroupHeader from '@/components/DataExtraction/ExtractionFieldGroupHeader';
import ReviewableExtractionField from '@/components/DataExtraction/ReviewableExtractionField';
import {
  DataExtractionFieldBase,
  DataExtractionFieldHeader,
  DataExtractionFieldMultipleSelect,
  DataExtractionReport,
  DataExtractionValueBase,
} from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'ReviewableExtractionFieldGroup',
  components: {
    ExtractionFieldGroupHeader,
    ReviewableExtractionField,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    headerField: {
      type: DataExtractionFieldHeader,
      required: false,
      default: null,
    },
    dataFields: {
      type: Array,
      required: true,
      validator: (field) =>
        typeHelpers.isArrayOfType(field, DataExtractionFieldBase),
    },
    extractionValues: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, DataExtractionValueBase),
    },
    reports: {
      type: Array,
      default: () => [],
      validator: (x) =>
        typeHelpers.isArrayOfType(x, DataExtractionReport),
    },
    finalReport: {
      type: DataExtractionReport,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errorFieldIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['input', 'confirm', 'expand-next'],
  computed: {
    expanded: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      },
    },
    extractionValuesByFieldId() {
      const extractionValuesByFieldId = {};
      this.extractionValues.forEach((extractionValue) => {
        const fieldId = extractionValue.field.id;
        const existing = extractionValuesByFieldId[fieldId] || [];
        extractionValuesByFieldId[fieldId] = [
          ...existing,
          extractionValue,
        ];
      });
      return extractionValuesByFieldId;
    },
    headerText() {
      return this.headerField?.title;
    },
    reportsByFieldId() {
      const reportMap = {};
      for (const report of this.reports) {
        for (const extractionValue of report.values) {
          const fieldId = extractionValue.field.id;
          if (!reportMap[fieldId]) {
            reportMap[fieldId] = [];
          }
          reportMap[fieldId].push(report);
        }
      }
      return reportMap;
    },
  },
  methods: {
    fieldHasFinalValue(fieldId) {
      return (
        this.finalReport?.values?.some(
          (extractionValue) => extractionValue.field.id === fieldId
        ) ?? false
      );
    },
    isArrayType(field) {
      return field instanceof DataExtractionFieldMultipleSelect;
    },
    finalValue(field) {
      const reportValues = this.finalReport?.values || [];
      const extractionValue = reportValues.find(
        (extractionValue) => extractionValue.field.id === field.id
      );

      if (!extractionValue) {
        return null;
      }
      if (this.isArrayType(field)) {
        return extractionValue.valueArr;
      }
      return extractionValue.value;
    },
    confirmAndExpandNextField(i, field, value) {
      const updatedFields = {};
      if (this.isArrayType(field)) {
        updatedFields.valueArr = value;
      } else {
        updatedFields.value = value;
      }
      this.$emit('confirm', { field, updatedFields });
      this.expanded = this.expanded.filter(
        (expanded) => expanded !== i
      );
      this.$emit('expand-next', i);
    },
  },
};
</script>
